import { Form, Formik } from 'formik';
import { FC } from 'react';

import { PhoneCodeVerificationInput } from '@/authentication/components/PhoneCodeVerificationInput/PhoneCodeVerificationInput';
import { PhoneVerificationAutoSubmit } from '@/authentication/components/PhoneVerificationAutoSubmit/PhoneVerificationAutoSubmit';
import { ResendCode } from '@/authentication/components/ResendCode/ResendCode';
import { CheckoutDescription } from '@/checkout/components/CheckoutDescription/CheckoutDescription';
import { CircularProgress } from '@/general/components/CircularProgress/CircularProgress';
import { FormikOnErrorTracking } from '@/general/components/forms/FormikOnErrorTracking/FormikOnErrorTracking';

import styles from './PersonalizedQuoteForm.module.scss';
import {
  PersonalizedQuoteFormProps,
  VerificationTokenFormValidation,
  VerificationTokenFormValues,
} from './PersonalizedQuoteForm.type';

const countdownLength = 30;
const verificationLength = 4;

const initialValues: VerificationTokenFormValues = {
  verificationCode: '',
  isRequestingNewCode: false,
};

export const VerificationTokenForm: FC<
  PersonalizedQuoteFormProps<VerificationTokenFormValues> & {
    phone: string;
    onResendTokenVerificationCode: () => Promise<void>;
  }
> = ({ onSubmit, values, onBack, phone, onResendTokenVerificationCode, onSubmitTracking }) => (
  <>
    <CheckoutDescription
      title="Enter verification code"
      description={
        <>
          We have sent a code to <b>{phone}</b>. This can take up to 2 minutes to receive
        </>
      }
      onBack={onBack}
    />
    <div className={styles.verifyTokenForm}>
      <Formik
        initialValues={{ ...initialValues, ...values }}
        validateOnChange
        validationSchema={VerificationTokenFormValidation}
        onSubmit={onSubmit}
      >
        {(props) => (
          <Form className={styles.form} noValidate>
            {!!onSubmitTracking && <FormikOnErrorTracking onError={onSubmitTracking} />}
            <PhoneVerificationAutoSubmit />
            <PhoneCodeVerificationInput
              fieldName="verificationCode"
              resendCodeFieldName="isRequestingNewCode"
              verificationLength={verificationLength}
              isSubmitting={props.isSubmitting}
            />
            {props.submitCount > 0 && props.errors.verificationCode && (
              <div className={styles.verificationCodeError}>{props.errors.verificationCode}</div>
            )}
            {props.isSubmitting ? (
              <div className={styles.loadingContainer}>
                <div className={styles.progressContainer}>
                  <CircularProgress />
                </div>
                <div className={styles.loadingText}>We are verifying your account</div>
              </div>
            ) : (
              <ResendCode
                fieldName="isRequestingNewCode"
                isSubmitting={props.isSubmitting}
                countdownLength={countdownLength}
                resendCode={onResendTokenVerificationCode}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  </>
);
